import {
  flushComposerDraftWriter,
  retireComposerDraftWriter,
} from '@/components/org-chat/composer-draft-writer';
import {
  composerDraftActorId,
  composerDraftStorageKey,
} from '@/components/org-chat/composer-draft-storage';

export type NavigationDraftCheckpoint = { key: string; value: string };
type DraftViewer = Parameters<typeof composerDraftActorId>[0];
/** The key the /design composer's draft writer uses for this viewer. */
export function newChatDraftKey(
  viewer: DraftViewer,
  orgSlug: string
): string | null {
  const actorId = composerDraftActorId(viewer);
  return actorId ? composerDraftStorageKey({ actorId, orgSlug }) : null;
}
export function captureBrowserNavigationDraft(
  viewer: DraftViewer,
  orgSlug: string
) {
  try {
    const key = newChatDraftKey(viewer, orgSlug);
    if (key) flushComposerDraftWriter(key);
    return captureNavigationDraft(window.sessionStorage, viewer, orgSlug);
  } catch {
    return undefined;
  }
}
export function clearBrowserNavigationDraft(
  checkpoint?: NavigationDraftCheckpoint
) {
  try {
    if (checkpoint) flushComposerDraftWriter(checkpoint.key);
    clearAcceptedNavigationDraft(window.sessionStorage, checkpoint);
  } catch {
    /* Private browsing can deny access to the storage object itself. */
  }
}
/**
 * The /design prompt was sent and the page is navigating away. Retire the
 * composer's draft writer first (its unmount flush would re-save the prompt
 * still in the editor), then drop the saved draft if nothing newer replaced
 * it. The delivery job keeps the prompt for retry if the send fails.
 */
export function discardSentNavigationDraft(
  viewer: DraftViewer,
  orgSlug: string,
  checkpoint?: NavigationDraftCheckpoint
) {
  try {
    const key = newChatDraftKey(viewer, orgSlug);
    if (key) retireComposerDraftWriter(key);
  } catch {
    /* A missing writer is fine; storage cleanup below still runs. */
  }
  clearBrowserNavigationDraft(checkpoint);
}
export function captureNavigationDraft(
  storage: Pick<Storage, 'getItem'>,
  viewer: DraftViewer,
  orgSlug: string
): NavigationDraftCheckpoint | undefined {
  try {
    const key = newChatDraftKey(viewer, orgSlug);
    if (!key) return undefined;
    const value = storage.getItem(key);
    return value === null ? undefined : { key, value };
  } catch {
    return undefined;
  }
}
/** Settlement may run after /design unmounts. Never erase a newer saved draft. */
export function clearAcceptedNavigationDraft(
  storage: Pick<Storage, 'getItem' | 'removeItem'>,
  checkpoint?: NavigationDraftCheckpoint
) {
  if (!checkpoint) return;
  try {
    if (storage.getItem(checkpoint.key) === checkpoint.value)
      storage.removeItem(checkpoint.key);
  } catch {
    /* Storage availability must not change a successful delivery receipt. */
  }
}
